import React, { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { RefreshCw, Clock } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { PingResponse } from 'public-contracts';

interface VersionUpdateNotificationProps {
  isOpen: boolean;
  currentVersion: PingResponse | null;
  newVersion: PingResponse | null;
  onRefresh: () => void;
  onDelay: () => void;
  autoRefreshIn?: number; // seconds until auto-refresh
}

export const VersionUpdateNotification: React.FC<
  VersionUpdateNotificationProps
> = ({
  isOpen,
  currentVersion,
  newVersion,
  onRefresh,
  onDelay,
  autoRefreshIn,
}) => {
  const { translate } = useTranslation();
  const [countdown, setCountdown] = useState(autoRefreshIn || 0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (isOpen && autoRefreshIn && autoRefreshIn > 0) {
      setCountdown(autoRefreshIn);
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isOpen, autoRefreshIn]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Clear cache and reload
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
    }
    onRefresh();
  };

  const formatVersion = (version: PingResponse | null) => {
    if (!version) return 'Unknown';
    return `${version.version} (Build ${version.buildNumber})`;
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent className="sm:max-w-[500px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            {translate('version.update.title', 'New Version Available')}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              {translate(
                'version.update.message',
                'A new version of the application is available. Please refresh to get the latest features and improvements.'
              )}
            </p>
            <div className="text-sm space-y-1 pt-2 border-t">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {translate('version.update.current', 'Current Version')}:
                </span>
                <span className="font-mono">
                  {formatVersion(currentVersion)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {translate('version.update.new', 'New Version')}:
                </span>
                <span className="font-mono font-semibold text-primary">
                  {formatVersion(newVersion)}
                </span>
              </div>
            </div>
            {countdown > 0 && (
              <div className="flex items-center gap-2 pt-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  {translate(
                    'version.update.auto_refresh',
                    'Auto-refreshing in {{countdown}} seconds...',
                    { countdown }
                  )}
                </span>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onDelay} disabled={isRefreshing}>
            <Clock className="h-4 w-4 mr-2" />
            {translate('version.update.delay', 'Remind Me Later')}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                {translate('version.update.refreshing', 'Refreshing...')}
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {translate('version.update.refresh', 'Refresh Now')}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
